// user-state.service.ts
import { Injectable, Provider } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProviderSearchFilters } from '../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { LocationSearchFilters } from '../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

@Injectable({ providedIn: 'root' })
export class SearchStateService {

    private searchModeSubject = new BehaviorSubject<'provider' | 'location'>('provider');
    searchMode$ = this.searchModeSubject.asObservable();

    private showSpinnerSubject = new BehaviorSubject<boolean>(false);
    showSpinner$ = this.showSpinnerSubject.asObservable();

    private providerSearchQuerySubject = new BehaviorSubject<{ nameOrSpecialty: string; cityOrZip: string } | null>(null);
    private providerSearchResultsSubject = new BehaviorSubject<any[] | null>(null);
    private providerSearchFiltersSubject = new BehaviorSubject<ProviderSearchFilters | null>(null);
    private providerSearchTypeAheadOptionsSubject = new BehaviorSubject<string[] | null>(null);
    private providerSearchFilterToggleSubject = new BehaviorSubject<boolean>(false);
    private currentProviderDetailsSubject = new BehaviorSubject<any | null>(null);
    private currentProviderDetailsLocationSubject = new BehaviorSubject<any | null>(null);
    
    private providerSortMethodSubject = new BehaviorSubject<'distance' | 'availability' | 'name'>('availability');
    private locationSortMethodSubject = new BehaviorSubject<'distance' | 'availability' | 'name'>('availability');

    private locationSearchQuerySubject = new BehaviorSubject<{ facilityOrLocationName: string; cityOrZip: string } | null>(null);
    private locationSearchResultsSubject = new BehaviorSubject<any[] | null>(null);
    private locationSearchFiltersSubject = new BehaviorSubject<LocationSearchFilters | null>(null);
    private locationSearchFilterToggleSubject = new BehaviorSubject<boolean>(false);
    private currentLocationDetailsSubject = new BehaviorSubject<any | null>(null);
    private currentLocationSubject = new BehaviorSubject<{ latitude: number; longitude: number } | null>(null);
    private currentLocationDetailsProviderSubject = new BehaviorSubject<any | null>(null);

    private cityTypeAheadOptionsSubject = new BehaviorSubject<string[] | null>(null);

    providerSearchQuery$ = this.providerSearchQuerySubject.asObservable();
    providerSearchResults$ = this.providerSearchResultsSubject.asObservable();
    providerSearchFilters$ = this.providerSearchFiltersSubject.asObservable();
    providerSearchTypeAheadOptions$ = this.providerSearchTypeAheadOptionsSubject.asObservable();
    providerSearchFilterToggle$ = this.providerSearchFilterToggleSubject.asObservable();
    providerDetails$ = this.currentProviderDetailsSubject.asObservable();
    providerDetailsLocation$ = this.currentProviderDetailsLocationSubject.asObservable();
    providerSortMethod$ = this.providerSortMethodSubject.asObservable();

    locationSearchQuery$ = this.locationSearchQuerySubject.asObservable();
    locationSearchResults$ = this.locationSearchResultsSubject.asObservable();
    locationSearchFilters$ = this.locationSearchFiltersSubject.asObservable();
    locationSearchFilterToggle$ = this.locationSearchFilterToggleSubject.asObservable();
    locationDetails$ = this.currentLocationDetailsSubject.asObservable();
    locationDetailsProvider$ = this.currentLocationDetailsProviderSubject.asObservable();
    locationSortMethod$ = this.locationSortMethodSubject.asObservable();

    cityTypeAheadOptions$ = this.cityTypeAheadOptionsSubject.asObservable();

    setSearchMode(mode: 'provider' | 'location') {
        this.searchModeSubject.next(mode);
        localStorage.setItem("providerLocationSearchMode", mode);
    }

    setShowSpinner(show: boolean) {
        this.showSpinnerSubject.next(show);
    }


    setProviderSearchQuery(providerSearchQuery: { nameOrSpecialty: string; cityOrZip: string }) {
        this.providerSearchQuerySubject.next(providerSearchQuery);
    }

    clearProviderSearchQuery() {
        this.providerSearchQuerySubject.next(null);
    }

    sortProviderSearchResults( results: any[], sortBy: 'distance' | 'availability' | 'name') {
        switch (sortBy) {
            case 'distance':
                return this.sortProviderSearchResultsByDistance(results);
            case 'availability':
                return this.sortProviderSearchResultsByAvailability(results);
            case 'name':
                return this.sortProviderSearchResultsByName(results);
            default:
                return results;
        }
    }

    private sortProviderSearchResultsByDistance(results: any[]) {
        // sort by distance, soonest available, then name
        // non-null values are prioritized over null values
        var sortedResults = results.sort((a: any, b: any) => {
            // Handle null distance values - nulls go to bottom
            if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
            if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
            // Compare by distance first
            const distanceComparison = a.distanceInMiles - b.distanceInMiles;
            if (distanceComparison !== 0) return distanceComparison;
            // If distances are equal, compare by availability
            if (a.nextAvailability == null && b.nextAvailability != null) return 1;
            if (a.nextAvailability != null && b.nextAvailability == null) return -1;
            const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
            if (availabilityComparison !== 0) return availabilityComparison;
            // If availabilities are equal, compare by name
            return a.providerName.localeCompare(b.providerName);
        });
        
        return sortedResults;
    }

    private sortProviderSearchResultsByAvailability(results: any[]) {
        // sort by soonest available, distance, then name
        // non-null values are prioritized over null values
        var sortedResults = results.sort((a: any, b: any) => {
            // If availabilities are equal, compare by distance
            if (a.nextAvailability == null && b.nextAvailability != null) return 1;
            if (a.nextAvailability != null && b.nextAvailability == null) return -1;
            const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
            if (availabilityComparison !== 0) return availabilityComparison;
            // Handle null distance values - nulls go to bottom
            if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
            if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
            // Compare by distance
            const distanceComparison = a.distanceInMiles - b.distanceInMiles;
            if (distanceComparison !== 0) return distanceComparison;
            // If distances are equal, compare by name
            return a.providerName.localeCompare(b.providerName);
        });
        return sortedResults;
    }

    private sortProviderSearchResultsByName(results: any[]) {
        // sort by name, distance, then soonest available
        var sortedResults = results.sort((a: any, b: any) => {  
            // Compare by name
            const nameComparison = a.providerName.localeCompare(b.providerName);
            if (nameComparison !== 0) return nameComparison;
            // Handle null distance values - nulls go to bottom
            if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
            if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
            // Compare by distance
            const distanceComparison = a.distanceInMiles - b.distanceInMiles;
            if (distanceComparison !== 0) return distanceComparison;
            // If distances are equal, compare by availability
            if (a.nextAvailability == null && b.nextAvailability != null) return 1;
            if (a.nextAvailability != null && b.nextAvailability == null) return -1;
            const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
            if (availabilityComparison !== 0) return availabilityComparison;
            return 0;
        });
        return sortedResults;
    }

    setProviderSearchResults(results: any[]) {
        this.providerSearchResultsSubject.next(results);
    }

    clearProviderSearchResults() {
        this.providerSearchResultsSubject.next(null);
    }

    setProviderSortMethod(sortBy: 'distance' | 'availability' | 'name') {
        this.providerSortMethodSubject.next(sortBy);
    }

    setProviderSearchFilters(filters: ProviderSearchFilters) {
        this.providerSearchFiltersSubject.next(filters);
    }

    clearProviderSearchFilters() {
        var currentFilters = this.providerSearchFiltersSubject.getValue();  
        if (currentFilters) {
            // Clear all selections 
            currentFilters.specialtyList?.forEach(option => option.isSelected = false);
            currentFilters.genderList?.forEach(option => option.isSelected = false);
            currentFilters.affiliationList?.forEach(option => option.isSelected = false);
            currentFilters.languageList?.forEach(option => option.isSelected = false);
            currentFilters.onlineSchedulingList?.forEach(option => option.isSelected = false);
            this.providerSearchFiltersSubject.next(currentFilters);
        }
    }

    setProviderSearchTypeAheadOptions(options: string[]) {
        this.providerSearchTypeAheadOptionsSubject.next(options);
    }

    clearProviderSearchTypeAheadOptions() {
        this.providerSearchTypeAheadOptionsSubject.next(null);
    }

    setProviderSearchFilterToggle(isOpen: boolean) {
        this.providerSearchFilterToggleSubject.next(isOpen);
    }

    getProviderSearchFilterToggle(): boolean {
        return this.providerSearchFilterToggleSubject.getValue();
    }

    toggleProviderSearchFilter() {
        const current = this.providerSearchFilterToggleSubject.getValue();
        this.providerSearchFilterToggleSubject.next(!current);
    }

    setCurrentProviderDetails(details: any) {
        this.currentProviderDetailsSubject.next(details);
    }

    clearCurrentProviderDetails() {
        this.currentProviderDetailsSubject.next(null);
    }

    setCurrentProviderDetailsLocation(details: any) {
        this.currentProviderDetailsLocationSubject.next(details);
    }

    clearCurrentProviderDetailsLocation() {
        this.currentProviderDetailsLocationSubject.next(null);
    }

    setLocationSearchQuery(locationSearchQuery: { facilityOrLocationName: string; cityOrZip: string }) {
        this.locationSearchQuerySubject.next(locationSearchQuery);
    }

    clearLocationSearchQuery() {
        this.locationSearchQuerySubject.next(null);
    }

    sortLocationSearchResults(results: any[], sortBy: 'distance' | 'availability' |'name') {
        switch (sortBy) {
            case 'distance':
                return this.sortLocationSearchResultsByDistance(results);
            case 'availability':
                return this.sortLocationSearchResultsByAvailability(results);
            case 'name':
                return this.sortLocationSearchResultsByName(results);
            default:
                return results;
        }
    }

    private sortLocationSearchResultsByDistance(results: any[]) {
        // sort by distance, then name
        // non-null values are prioritized over null values
        var sortedResults = results.sort((a: any, b: any) => {
            // Handle null distance values - nulls go to bottom
            if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
            if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
            // Compare by distance first
            const distanceComparison = a.distanceInMiles - b.distanceInMiles;
            if (distanceComparison !== 0) return distanceComparison;

            // compare by next availability
            if (a.nextAvailability == null && b.nextAvailability != null) return 1;
            if (a.nextAvailability != null && b.nextAvailability == null) return -1;
            const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
            if (availabilityComparison !== 0) return availabilityComparison;

            // compare by wait time
            if (a.waitTime == null && b.waitTime != null) return 1;
            if (a.waitTime != null && b.waitTime == null) return -1;
            const waittimeComparison = a.waitTime- b.waitTime;
            if (waittimeComparison !== 0) return waittimeComparison;
            
            // If distances are equal, compare by name
            return a.locationName.localeCompare(b.locationName);
        });
        
        return sortedResults;
    }

    private sortLocationSearchResultsByAvailability(results: any[]) {
        // sort by next availability, then distance, then name
        // non-null values are prioritized over null values
        var sortedResults = results.sort((a: any, b: any) => {
            // compare by next availability
            if (a.nextAvailability == null && b.nextAvailability != null) return 1;
            if (a.nextAvailability != null && b.nextAvailability == null) return -1;
            const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
            if (availabilityComparison !== 0) return availabilityComparison;
            
            // compare by wait time
            if (a.waitTime == null && b.waitTime != null) return 1;
            if (a.waitTime != null && b.waitTime == null) return -1;
            const waittimeComparison = a.waitTime- b.waitTime;
            if (waittimeComparison !== 0) return waittimeComparison;
            
            // Handle null distance values - nulls go to bottom
            if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
            if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
            // Compare by distance first
            const distanceComparison = a.distanceInMiles - b.distanceInMiles;
            if (distanceComparison !== 0) return distanceComparison;
            // If distances are equal, compare by name
            return a.locationName.localeCompare(b.locationName);
        });
        
        return sortedResults;
    }

    private sortLocationSearchResultsByName(results: any[]) {
        // sort by name, next availability, then distance
        var sortedResults = results.sort((a: any, b: any) => {  
            // Compare by name
            const nameComparison = a.locationName.localeCompare(b.locationName);
            if (nameComparison !== 0) return nameComparison;
            // compare by next availability
            if (a.nextAvailability == null && b.nextAvailability != null) return 1;
            if (a.nextAvailability != null && b.nextAvailability == null) return -1;
            const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
            if (availabilityComparison !== 0) return availabilityComparison;

            // compare by wait time
            if (a.waitTime == null && b.waitTime != null) return 1;
            if (a.waitTime != null && b.waitTime == null) return -1;
            const waittimeComparison = a.waitTime- b.waitTime;
            if (waittimeComparison !== 0) return waittimeComparison;
            
            // Handle null distance values - nulls go to bottom
            if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
            if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
            // Compare by distance
            const distanceComparison = a.distanceInMiles - b.distanceInMiles;
            if (distanceComparison !== 0) return distanceComparison;
            return 0;
        });
        return sortedResults;
    }

    setLocationSearchResults(results: any[]) {
        this.locationSearchResultsSubject.next(results);
    }

    clearLocationSearchResults() {
        this.locationSearchResultsSubject.next(null);
    }   

    setLocationSortMethod(sortBy: 'distance' | 'availability' |  'name') {
        this.locationSortMethodSubject.next(sortBy);
    }

    setLocationSearchFilters(filters: LocationSearchFilters) {
        this.locationSearchFiltersSubject.next(filters);
    }

    clearLocationSearchFilters() {
        var currentFilters = this.locationSearchFiltersSubject.getValue();  
        if (currentFilters) {
            // Clear all selections
            currentFilters.typeList?.forEach(option => option.isSelected = false);
            currentFilters.serviceList?.forEach(option => option.isSelected = false);   
            this.locationSearchFiltersSubject.next(currentFilters);
        }
    }

    setLocationSearchFilterToggle(isOpen: boolean) {
        this.locationSearchFilterToggleSubject.next(isOpen);
    }

    toggleLocationSearchFilter() {
        const current = this.locationSearchFilterToggleSubject.getValue();
        this.locationSearchFilterToggleSubject.next(!current);
    }

    setCurrentLocationDetails(details: any) {
        this.currentLocationDetailsSubject.next(details);
    }

    clearCurrentLocationDetails() {
        this.currentLocationDetailsSubject.next(null);
    }

    setCurrentLocationDetailsProvider(details: any) {
        this.currentLocationDetailsProviderSubject.next(details);
    }

    clearCurrentLocationDetailsProvider() {
        this.currentLocationDetailsProviderSubject.next(null);
    }

    setCurrentLocation(location: { latitude: number; longitude: number }) {
        this.currentLocationSubject.next(location);
    }

    getCurrentLocation(): { latitude: number; longitude: number } | null {
        return this.currentLocationSubject.getValue();
    }

    clearCurrentLocation() {
        this.currentLocationSubject.next(null);
    }

    setCityTypeAheadOptions(options: string[]) {
        this.cityTypeAheadOptionsSubject.next(options);
    }

    clearCityTypeAheadOptions() {
        this.cityTypeAheadOptionsSubject.next(null);
    }

    // sortLocationSearchResults( results: any[], sortBy: 'distance' | 'availability' | 'name') {
    //     switch (sortBy) {
    //         case 'distance':
    //             return this.sortLocationSearchResultsByDistance(results);
    //         case 'availability':
    //             return this.sortLocationSearchResultsByAvailability(results);
    //         case 'name':
    //             return this.sortLocationSearchResultsByName(results);
    //         default:
    //             return results;
    //     }
    // }

    // private sortLocationSearchResultsByDistance(results: any[]) {
    //     // sort by distance, soonest available, then name
    //     // non-null values are prioritized over null values
    //     var sortedResults = results.sort((a: any, b: any) => {
    //         // Handle null distance values - nulls go to bottom
    //         if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
    //         if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
    //         // Compare by distance first
    //         const distanceComparison = a.distanceInMiles - b.distanceInMiles;
    //         if (distanceComparison !== 0) return distanceComparison;
    //         // If distances are equal, compare by availability
    //         if (a.nextAvailability == null && b.nextAvailability != null) return 1;
    //         if (a.nextAvailability != null && b.nextAvailability == null) return -1;
    //         const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
    //         if (availabilityComparison !== 0) return availabilityComparison;
    //         // If availabilities are equal, compare by name
    //         return a.locationName.localeCompare(b.locationName);
    //     });
        
    //     return sortedResults;
    // }

    // private sortLocationSearchResultsByAvailability(results: any[]) {
    //     // sort by soonest available, distance, then name
    //     // non-null values are prioritized over null values
    //     var sortedResults = results.sort((a: any, b: any) => {
    //         // If availabilities are equal, compare by distance
    //         if (a.nextAvailability == null && b.nextAvailability != null) return 1;
    //         if (a.nextAvailability != null && b.nextAvailability == null) return -1;
    //         const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
    //         if (availabilityComparison !== 0) return availabilityComparison;
    //         // Handle null distance values - nulls go to bottom
    //         if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
    //         if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
    //         // Compare by distance
    //         const distanceComparison = a.distanceInMiles - b.distanceInMiles;
    //         if (distanceComparison !== 0) return distanceComparison;
    //         // If distances are equal, compare by name
    //         return a.locationName.localeCompare(b.locationName);
    //     });
    //     return sortedResults;
    // }

    // private sortLocationSearchResultsByName(results: any[]) {
    //     // sort by name, distance, then soonest available
    //     var sortedResults = results.sort((a: any, b: any) => {  
    //         // Compare by name
    //         const nameComparison = a.locationName.localeCompare(b.locationName);
    //         if (nameComparison !== 0) return nameComparison;
    //         // Handle null distance values - nulls go to bottom
    //         if (a.distanceInMiles == null && b.distanceInMiles != null) return 1;
    //         if (a.distanceInMiles != null && b.distanceInMiles == null) return -1;
    //         // Compare by distance
    //         const distanceComparison = a.distanceInMiles - b.distanceInMiles;
    //         if (distanceComparison !== 0) return distanceComparison;
    //         // If distances are equal, compare by availability
    //         if (a.nextAvailability == null && b.nextAvailability != null) return 1;
    //         if (a.nextAvailability != null && b.nextAvailability == null) return -1;
    //         const availabilityComparison = new Date(a.nextAvailability).getTime() - new Date(b.nextAvailability).getTime();
    //         if (availabilityComparison !== 0) return availabilityComparison;
    //         return 0;
    //     });
    //     return sortedResults;
    // }
}
